import type { TreeViewBaseItem } from "@mui/x-tree-view/models";
import type { CachedTopic } from "../slice";
import type { TopicTreeNode } from "../hooks/useBuildTopicTree";

export const toTopicNodeId = (topicId: string, collectionId: string): string =>
  `topic:${topicId}:${collectionId}`;
export const toCollectionNodeId = (id: string): string => `collection:${id}`;

export const fromTopicNodeId = (id: string): string => {
  const match = id.match(/^topic:([^:]+):/);
  return match ? match[1] : id;
};

export type TreeItemsResult = {
  items: TreeViewBaseItem[];
  topicNodeMap: Map<string, string[]>;
};

export function buildTreeViewItems(
  nodes: TopicTreeNode[],
  rootTopics: CachedTopic[]
): TreeItemsResult {
  const topicNodeMap = new Map<string, string[]>();

  const buildItems = (treeNodes: TopicTreeNode[]): TreeViewBaseItem[] =>
    treeNodes.map((node) => {
      const children: TreeViewBaseItem[] = [];
      const sortedTopics = [...node.topics].sort((a, b) =>
        a.displayName.localeCompare(b.displayName)
      );

      sortedTopics.forEach((topic) => {
        const nodeId = toTopicNodeId(topic.id, node.id);
        children.push({
          id: nodeId,
          label: topic.displayName,
        });
        const existing = topicNodeMap.get(topic.id) ?? [];
        existing.push(nodeId);
        topicNodeMap.set(topic.id, existing);
      });

      const sortedChildren = [...node.children].sort((a, b) =>
        a.displayName.localeCompare(b.displayName)
      );
      children.push(...buildItems(sortedChildren));

      return {
        id: toCollectionNodeId(node.id),
        label: node.displayName,
        children,
      };
    });

  const rootTopicItems: TreeViewBaseItem[] = rootTopics.map((topic) => {
    const nodeId = toTopicNodeId(topic.id, "root");
    const existing = topicNodeMap.get(topic.id) ?? [];
    existing.push(nodeId);
    topicNodeMap.set(topic.id, existing);
    return {
      id: nodeId,
      label: topic.displayName,
    };
  });

  const rootCollectionItems = buildItems(nodes);

  const rootItems = [...rootTopicItems, ...rootCollectionItems].sort((a, b) => {
    const labelA = (a.label?.toString() ?? "").toLowerCase();
    const labelB = (b.label?.toString() ?? "").toLowerCase();
    return labelA.localeCompare(labelB);
  });

  return { items: rootItems, topicNodeMap };
}

const areAllTopicsSelected = (
  node: TopicTreeNode,
  selectedTopicIds: Set<string>
): boolean => {
  const topicsSelected = node.topics.every((topic) =>
    selectedTopicIds.has(topic.id)
  );
  const childrenSelected = node.children.every((child) =>
    areAllTopicsSelected(child, selectedTopicIds)
  );
  return topicsSelected && childrenSelected;
};

export function collectFullySelectedCollections(
  nodes: TopicTreeNode[],
  selectedTopicIds: Set<string>
): string[] {
  const selectedCollections: string[] = [];

  nodes.forEach((node) => {
    if (areAllTopicsSelected(node, selectedTopicIds)) {
      selectedCollections.push(node.id);
    }
    selectedCollections.push(
      ...collectFullySelectedCollections(node.children, selectedTopicIds)
    );
  });

  return selectedCollections;
}
